import { createStore } from '~/state/createStore';

export type MfeLoadEvent = {
  componentName: string;
  // 'loaded'  — bundle registered in globalThis (framework-emitted, too early for interactions)
  // 'ready'   — MFE app initialized and safe to call (application-emitted)
  // 'teardown'— MFE app unmounting (application-emitted, pre-REO-8289 framework support)
  data: 'error' | 'loaded' | 'ready' | 'teardown' | 'timeout';
  moduleName: string;
  timestamp: number;
};

type MfeModuleState = {
  isLoaded: boolean;
  isReady: boolean;
  loadCount: number;
};

export type MfeLoadState = {
  modules: Record<string, MfeModuleState>;
  record: (event: MfeLoadEvent) => void;
};

/**
 * Zustand store that tracks MFE lifecycle state, keyed by module name.
 * Populated by MfeEventsListener, which must be mounted in the page's layout.
 *
 * Pages with MFE load state available (MfeEventsListener is in their layout):
 *   /cars, /vehicle, /shareyourcarvana  — all under (carvana-layout)
 *
 * Pages WITHOUT MFE load state (MfeEventsListener not present):
 *   / (Home, Pages Router), /(financing), /dealership, /vending-machine
 *
 * If you use this store or useAccountModalReady on a page outside (carvana-layout),
 * add MfeEventsListener to that page's layout first — otherwise MFE ready state
 * will always be false and any gated actions will never execute.
 *
 * TODO: migrate SoftPullModule readiness into this store. useToggleModal currently
 * manages its own isReady state by subscribing to softpullModal.ready directly.
 * Adding softpullModal.ready as a listener in MfeEventsListener (recording it as
 * data: 'ready' for 'SoftPullModule') would consolidate the pattern and eliminate
 * the duplicated useToggleModal implementations in /common/ and /srp/.
 */
export const useMfeLoadStore = createStore<MfeLoadState>(
  'MfeLoadState',
  set => ({
    modules: {},
    record: event =>
      set(state => {
        const prev = state.modules[event.moduleName] ?? {
          isLoaded: false,
          isReady: false,
          loadCount: 0
        };

        return {
          modules: {
            ...state.modules,
            [event.moduleName]: {
              isLoaded: prev.isLoaded || event.data === 'loaded',
              isReady:
                event.data === 'ready'
                  ? true
                  : event.data === 'teardown'
                  ? false
                  : prev.isReady,
              loadCount: prev.loadCount + (event.data === 'loaded' ? 1 : 0)
            }
          }
        };
      })
  })
);

export const selectIsMfeLoaded =
  (moduleName: string) => (state: MfeLoadState) =>
    state.modules[moduleName]?.isLoaded ?? false;

export const selectIsMfeReady = (moduleName: string) => (state: MfeLoadState) =>
  state.modules[moduleName]?.isReady ?? false;

export const selectMfeLoadCount =
  (moduleName: string) => (state: MfeLoadState) =>
    state.modules[moduleName]?.loadCount ?? 0;
